/**
 * LA DESCARGA COMPLETA: deja en el teléfono todo lo que el usuario puede ver,
 * para que sin cobertura la app siga enseñándolo (como WhatsApp con sus chats).
 *
 * Cinco fases, en este orden (lo más útil primero, lo más pesado al final):
 *
 *   1. DATOS   — la copia de las tablas de la empresa, toda la historia
 *                (`espejoOffline`). Con ella cualquier pantalla contesta sin red.
 *   1b. CÓDIGO — el código de todas las pantallas (`precargaOffline`): sin él,
 *                una pantalla no abierta en esta sesión no podría pintarse.
 *   2. CÁLCULOS — las RPC que solo sabe hacer el servidor y que pintan el mapa y
 *                las naves: IRM, plazo de seguridad y asignados de cada sector,
 *                interruptores de la empresa (`redOffline` guarda la respuesta).
 *   3. FOTOS   — fotos y audios de muestreos, fotos de visitas, avatares y logo.
 *   4. MAPA    — las teselas del satélite de todas las naves, a los zooms en que
 *                se mira una nave.
 *
 * Corre en segundo plano (`SincronizacionOffline`): al entrar, cada poco con la
 * app abierta y al volver la red. Cada tabla y cada cosa tiene su ritmo, así que
 * una pasada «sin nada nuevo que bajar» apenas gasta datos. Se para si se pierde
 * la red o cambia el usuario.
 */
import { supabase } from "@/integrations/supabase/client";
import { guardar, leer, espacioUsado } from "./almacenOffline";
import { esperarAppQuieta, modoActivo, redDudosa, sinRedSegura, usuarioOffline } from "./estadoOffline";
import { descargarEspejo, filasGuardadas, type ResultadoTabla } from "./espejoOffline";
import { descargarFotos } from "./fotosOffline";
import { cajaDe, descargarTeselas, teselasDeCaja } from "./teselasOffline";
import { precargarPantallas } from "./precargaOffline";
import type { Fila } from "./consultaLocal";

export type Fase = "datos" | "codigo" | "calculos" | "fotos" | "mapa";

export interface EstadoDescarga {
  enCurso: boolean;
  fase: Fase | null;
  hechas: number;
  total: number;
  /** Última descarga completa terminada (ms). */
  ultima: number | null;
  /** Tablas que fallaron en la última pasada. */
  fallos: string[];
}

let estado: EstadoDescarga = { enCurso: false, fase: null, hechas: 0, total: 0, ultima: null, fallos: [] };
const oyentes = new Set<() => void>();

function cambiar(parcial: Partial<EstadoDescarga>): void {
  estado = { ...estado, ...parcial };
  oyentes.forEach((f) => {
    try {
      f();
    } catch {
      /* nada */
    }
  });
}
export const leerEstadoDescarga = (): EstadoDescarga => estado;
export function suscribirEstadoDescarga(f: () => void): () => void {
  oyentes.add(f);
  return () => oyentes.delete(f);
}

/** Cada cuánto se repiten los cálculos por sector (cambian con cada tratamiento). */
const CADA_CALCULOS_MS = 60 * 60 * 1000;
/** Fotos y teselas: se miran como mucho cada 2 h (solo bajan lo que falta). */
const CADA_ARCHIVOS_MS = 2 * 60 * 60 * 1000;
/** Los zooms en que se mira una nave en el mapa (el satélite llega a z20). */
export const ZOOMS_NAVE = [15, 16, 17, 18, 19, 20];

async function toca(uid: string, que: string, cada: number, forzar: boolean): Promise<boolean> {
  if (forzar) return true;
  const m = await leer<{ at: number }>("meta", uid, `descarga:${que}`);
  return !m || Date.now() - m.at > cada;
}
const hecho = (uid: string, que: string) => guardar("meta", uid, `descarga:${que}`, { at: Date.now() });

/* ─── Fase 2: cálculos del servidor ─────────────────────────────────────── */

type Rpc = { rpc: (n: string, a?: Record<string, unknown>) => PromiseLike<unknown> };

async function descargarCalculos(empresa: string, rol: string | null): Promise<void> {
  const db = supabase as unknown as Rpc;
  const llamar = (n: string, a?: Record<string, unknown>) => Promise.resolve(a ? db.rpc(n, a) : db.rpc(n)).catch(() => undefined);
  // Interruptores: los pide casi cualquier pantalla al abrirse.
  await Promise.all([
    llamar("pest_crop_ai_activo"),
    llamar("pest_crop_lists_activo"),
    llamar("pest_zones_activo"),
    llamar("precio_pendiente_activo"),
    llamar("catalogo_cultivos_activo"),
    llamar("_registro_mapa_off", { p_company: empresa }),
  ]);
  if (rol === "worker") return; // el trabajador no ve el mapa ni las fichas de sector
  await llamar("get_irm_overview");
  const bloques = (await filasGuardadas("blocks")).filter((b) => !b.archived_at && b.company_id === empresa);
  for (let i = 0; i < bloques.length; i += 2) {
    if (sinRedSegura() || !modoActivo()) return;
    await esperarAppQuieta();
    await Promise.all(
      bloques.slice(i, i + 2).flatMap((b) => [
        // Los mismos argumentos, con el mismo nombre, que las pantallas (useBlockPhiStatus,
        // SectionDetailDrawer, GreenhouseDetail): la copia se busca por la petición exacta.
        llamar("get_block_phi_status", { target_block_id: b.id }),
        llamar("get_block_assignments", { p_block_id: b.id }),
      ]),
    );
  }
}

/* ─── Fase 3: fotos y audios ─────────────────────────────────────────────── */

function urlsDe(v: unknown, out: Set<string>): void {
  if (!v) return;
  if (typeof v === "string") {
    if (v.startsWith("https://")) out.add(v);
    return;
  }
  if (Array.isArray(v)) {
    v.forEach((x) => urlsDe(x, out));
    return;
  }
  if (typeof v === "object") {
    const o = v as Fila;
    for (const k of ["url", "photo_url", "publicUrl", "src"]) urlsDe(o[k], out);
    if (Array.isArray(o.photos)) urlsDe(o.photos, out);
  }
}

export async function urlsDeArchivos(): Promise<string[]> {
  const out = new Set<string>();
  for (const s of await filasGuardadas("pest_scouting_sessions")) {
    urlsDe(s.photo_url, out);
    urlsDe(s.photo_urls, out);
    urlsDe(s.audio_url, out);
  }
  for (const t of await filasGuardadas("planner_tasks")) {
    urlsDe(t.photos, out);
    if (t.visit_report && typeof t.visit_report === "object") urlsDe((t.visit_report as Fila).photos, out);
  }
  for (const p of await filasGuardadas("profiles")) urlsDe(p.avatar_url, out);
  for (const c of await filasGuardadas("companies")) urlsDe(c.logo_url, out);
  return Array.from(out);
}

/* ─── Fase 4: el satélite de las naves ───────────────────────────────────── */

export async function teselasDeLasNaves(empresa: string): Promise<string[]> {
  const claves = new Set<string>();
  for (const g of await filasGuardadas("greenhouses")) {
    if (g.company_id !== empresa) continue;
    const geo = (g.geometry as { geoPolygonWgs84?: unknown } | null)?.geoPolygonWgs84;
    let anillo: Array<[number, number] | { lat: number; lng: number }> | null = null;
    if (Array.isArray(geo)) {
      // [[lng,lat],…] o [[[lng,lat],…]] (polígono con anillos)
      anillo = (Array.isArray(geo[0]) && Array.isArray((geo[0] as unknown[])[0]) ? geo[0] : geo) as Array<[number, number]>;
    } else if (geo && typeof geo === "object" && Array.isArray((geo as { coordinates?: unknown }).coordinates)) {
      anillo = ((geo as { coordinates: Array<Array<[number, number]>> }).coordinates[0] ?? null) as Array<[number, number]>;
    }
    if (!anillo && typeof g.centroid_lat === "number" && typeof g.centroid_lng === "number") {
      anillo = [{ lat: g.centroid_lat, lng: g.centroid_lng }];
    }
    const caja = anillo ? cajaDe(anillo) : null;
    if (caja) teselasDeCaja(caja, ZOOMS_NAVE).forEach((k) => claves.add(k));
  }
  return Array.from(claves);
}

/* ─── Datos móviles ──────────────────────────────────────────────────────── */

/**
 * ¿El teléfono pide ahorrar datos o va por 2G? Entonces no se bajan fotos ni
 * satélite (lo pesado); los datos sí. `navigator.connection` solo existe en
 * Android (Chrome/WebView); en iOS no se sabe y se baja.
 */
export function ahorroDeDatos(): boolean {
  try {
    const c = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    return !!c && (c.saveData === true || c.effectiveType === "2g" || c.effectiveType === "slow-2g");
  } catch {
    return false;
  }
}

/* ─── La pasada entera ───────────────────────────────────────────────────── */

export interface OpcionesDescarga {
  empresa: string;
  rol: string | null;
  forzar?: boolean;
}

/**
 * Una pasada de descarga. No hace nada si ya hay una en curso, si no hay red o
 * si no hay usuario. Devuelve el resultado de las tablas (para las pruebas).
 */
export async function sincronizar(op: OpcionesDescarga): Promise<ResultadoTabla[]> {
  const uid = usuarioOffline();
  if (!uid || !modoActivo() || estado.enCurso || sinRedSegura() || !op.empresa) return [];
  // Con la red floja (fallos recientes, o aún sin ninguna respuesta de verdad) no se
  // descarga: se le dejaría sin red a lo que el usuario está mirando. Otra pasada lo hará.
  if (redDudosa() && !op.forzar) return [];
  const forzar = !!op.forzar;
  cambiar({ enCurso: true, fase: "datos", hechas: 0, total: 0, fallos: [] });
  let tablas: ResultadoTabla[] = [];
  try {
    tablas = await descargarEspejo({
      empresa: op.empresa,
      forzar,
      alAvanzar: (hechas, total) => cambiar({ hechas, total }),
    });
    const fallos = tablas.filter((t) => !t.ok && !t.saltada).map((t) => t.tabla);
    cambiar({ fallos });
    if (usuarioOffline() !== uid) return tablas;

    cambiar({ fase: "codigo", hechas: 0, total: 0 });
    await precargarPantallas();
    if (usuarioOffline() !== uid) return tablas;

    if (await toca(uid, "calculos", CADA_CALCULOS_MS, forzar)) {
      cambiar({ fase: "calculos", hechas: 0, total: 0 });
      await descargarCalculos(op.empresa, op.rol);
      if (!sinRedSegura()) await hecho(uid, "calculos");
    }
    if (usuarioOffline() !== uid) return tablas;

    /* Lo pesado (fotos y satélite): nunca con ahorro de datos, de dos en dos, esperando
       a que la app esté quieta, y con tope por pasada. Solo se da por hecho cuando no
       queda nada; si quedan, la próxima pasada (cada pocos minutos) sigue. */
    const pesado = !ahorroDeDatos();
    if (pesado && (await toca(uid, "fotos", CADA_ARCHIVOS_MS, forzar))) {
      const urls = await urlsDeArchivos();
      cambiar({ fase: "fotos", hechas: 0, total: urls.length });
      const { quedan } = await descargarFotos(urls, { antesDeCada: () => esperarAppQuieta() });
      if (!sinRedSegura() && quedan === 0) await hecho(uid, "fotos");
    }
    if (usuarioOffline() !== uid) return tablas;

    if (pesado && op.rol !== "worker" && (await toca(uid, "mapa", CADA_ARCHIVOS_MS, forzar))) {
      const claves = await teselasDeLasNaves(op.empresa);
      cambiar({ fase: "mapa", hechas: 0, total: claves.length });
      const { quedan } = await descargarTeselas(claves, { antesDeCada: () => esperarAppQuieta() });
      if (!sinRedSegura() && quedan === 0) await hecho(uid, "mapa");
    }

    // «Guardado» = los datos (lo que de verdad se consulta sin red); fotos y satélite
    // pueden seguir llegando en las próximas pasadas.
    if (!sinRedSegura() && fallos.length === 0) {
      await guardar("meta", uid, "descarga:ultima", { at: Date.now() });
      cambiar({ ultima: Date.now() });
    }
    return tablas;
  } finally {
    cambiar({ enCurso: false, fase: null });
  }
}

/** Para la ficha «Datos en este móvil». */
export async function resumenDescarga(): Promise<{ ultima: number | null; usado: number | null }> {
  const uid = usuarioOffline();
  if (!uid) return { ultima: null, usado: null };
  const m = await leer<{ at: number }>("meta", uid, "descarga:ultima");
  const { usado } = await espacioUsado();
  if (m?.at && m.at !== estado.ultima) cambiar({ ultima: m.at });
  return { ultima: m?.at ?? null, usado };
}
